import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  EventEmitter,
  Output, ChangeDetectorRef
} from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import { Store } from '@ngrx/store';
import 'rxjs/add/operator/switchMapTo';

import {
  PageEventsHandler, 
  SocketHandler,
  Translate,
  Utils,
} from '../../services';
import {
  UserActions,
  UserService, 
  ViewActions,
  ViewService,
  AssessmentService,
} from '../../logics';
import {
  FAKE_LOGIN_DELAY_MS,
  REQUEST_FULLSCREEN,
  CHECK_DEV_TOOLS,
  DISABLE_SHORTCUT,
  TRAINING_MODE,
  TIMEOUT_IDLE_LOGIN, 
  WELCOME_PAGE,
  USER_INIT_USERNAME,
  USER_INIT_PASSWORD,
} from '../../constants';

@Component({
  selector: 'login-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div 
      class="cs-login"
      *ngIf="!trainingMode && !(locked$ | async)">
      <div class="cs-modal-bg cs-login-modal-bg">
      </div>
      <div class="cs-modal cs-login-modal">
        <h1>{{ (translations$ | async)['login#title'] }}</h1>
        <h2>{{ (translations$ | async)['login#sub_title'] }}</h2>
        <form 
          #formRef="ngForm" 
          (ngSubmit)="loginSubmit$.next(formRef.value)">
          <div>
            <label 
              class="cs-form-input-label" 
              for="cisia-email">
              {{ (translations$ | async)['login#email_label'] }}
            </label>
            <input 
              type="text" 
              name="email"
              id="cisia-email" 
              autocapitalize="none"
              autocomplete="off"
              autocorrect="off"
              spellcheck="false" 
              placeholder="{{ (translations$ | async)['login#email_placeholder'] }}"
              (focus)="onFocus()"
              (blur)="onBlur()"
              [ngModel]="usr">
          </div>
          <div>
            <label 
              class="cs-form-input-label" 
              for="cisia-password">
              {{ (translations$ | async)['login#password_label'] }}
            </label>
            <input 
              [type]="isPasswordType ? 'password' : 'text'"
              name="password" 
              id="cisia-password" 
              autocapitalize="none"
              autocomplete="new-password"
              autocorrect="off"
              spellcheck="false" 
              placeholder="{{ (translations$ | async)['login#password_placeholder'] }}"
              (focus)="onFocus()"
              (blur)="onBlur()"
              [ngModel]="psw">
            <a class="cs-form-password-toggle" (click)="toggleInputType($event)">
              <span *ngIf="isPasswordType">{{ (translations$ | async)['login#show_password'] }}</span>
              <span *ngIf="!isPasswordType">{{ (translations$ | async)['login#hide_password'] }}</span>
            </a>
          </div>
          <div 
              class="cs-form-warning-message"
              *ngIf="showSocketWarningMessage && !inhibitConnectionProblemMessage"
          >
            <div>
            {{ (translations$ | async)['login#connection_error_line0'] }}  
            </div>
            <div>
            {{ (translations$ | async)['login#connection_error_line1'] }}  
            </div>
          </div>
          <div class="cs-form-btn-group">
            <button 
              class="cs-btn cs-btn-cta"
              [disabled]="!isConnectedToServer"
              [ngClass]="{
                'cs-btn-loader': (isLoading$ | async)
              }"
              type="submit">
              {{ (isLoading$ | async) ? '' : (translations$ | async)['login#confirm'] }}
              <div
                class="cs-loader"
                *ngIf="(isLoading$ | async)">
                  <div class="bounce1"></div>
                  <div class="bounce2"></div>
                  <div class="bounce3"></div>
              </div>
            </button>
          </div>
        </form>
		    <cs-lang-switch></cs-lang-switch>
      </div>
    </div>
    <div class="cs-simple-page">
      <div
          class="cs-simple-page__content"
          *ngIf="locked$ | async">
        <p class="cs-simple-page__text">
          {{ (translations$ | async)['login#test_locked']}}
        </p>
        <button
            class="cs-btn cs-btn-cta cs-btn-xl cs-btn-start-test"
            (click)="refreshPageClick()">
          {{ (translations$ | async)['login#back_to_login']}}
        </button>
      </div>
    </div>

    <cs-loader *ngIf="trainingMode">
    </cs-loader>
  `
})

export class LoginComponent implements OnInit, OnDestroy {
  @Output() viewChange = new EventEmitter<string>();

  destroyed$: Subject<any> = new Subject<any>();
  loginSubmit$: Subject<any> = new Subject<any>();
  isLoading$: Observable<boolean>;
  isModalActive$: Observable<boolean>;
  translations$: Observable<any> = this.translate.getTranslations$();
  locked$: Observable<any>;
  isPasswordType: boolean = true;

  trainingMode: boolean = TRAINING_MODE;

  isConnectedToServer: boolean = false;
  showSocketWarningMessage: boolean = false;

  inhibitConnectionProblemMessage: boolean = false;
  userInputDisabled: boolean = false;

  usr = USER_INIT_USERNAME;
  psw = USER_INIT_PASSWORD;

  requestFullscreen: boolean;

  constructor(
    private store: Store<any>,
    private utils: Utils,
    private userActions: UserActions,
    private viewActions: ViewActions,
    private userService: UserService,
    private viewService: ViewService,
    private assessmentService: AssessmentService,
    private socketHandler: SocketHandler,
    private pageEventsHandler: PageEventsHandler,
    private translate: Translate,
    private chRef: ChangeDetectorRef
  ) {
    this.isLoading$ = this.userService.isLoading$();
    this.isModalActive$ = this.viewService.isModalVisible$();
    this.locked$ = this.assessmentService.isLocked$();

    // global user control
    if(typeof window['overrideableVar'] === 'object' && window['overrideableVar'].user){
      this.usr = window['overrideableVar'].user;
      this.userInputDisabled = true;
    }

    this.requestFullscreen = REQUEST_FULLSCREEN && this.utils.isWeb();
  }

  ngOnInit() {
    this.isLoading$
      .switchMap(loading => loading
        ? Observable.never()
        : this.loginSubmit$)
      .filter(() => !CHECK_DEV_TOOLS ||
        CHECK_DEV_TOOLS && !this.pageEventsHandler.areDevToolsOpen())
      .filter(({email, password}) => email !== '' && password !== '')
      .takeUntil(this.destroyed$)
      .subscribe(({email, password}) => {
          // overrideableVar usr control
          email = (this.userInputDisabled && this.usr && !email) ? this.usr : email;
          this.store.dispatch(this.viewActions.resetAlerts());
          this.store.dispatch(this.userActions.login(email, password));
        }
      );

    this.userService.isLoaded$()
      .withLatestFrom(this.viewService.viewName$())
      .filter(([, viewName]) => viewName !== 'survey')
      .delay(FAKE_LOGIN_DELAY_MS)
      .takeUntil(this.destroyed$)
      .subscribe(() => this.viewChange.emit('wait'));

    this.viewService.isFullscreen$()
      .filter(() => this.requestFullscreen)
      .takeUntil(this.destroyed$)
      .subscribe(isFullscreen => {
        if (!isFullscreen) {
          this.viewChange.emit('wait');
        }
      });

    this.socketHandler.connect$
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.isConnectedToServer = true;
        this.showSocketWarningMessage = !this.isConnectedToServer;
        this.chRef.detach();
        Observable.timer(0, 10).take(1).subscribe((tick) => {
          this.chRef.detectChanges();
          this.chRef.reattach();
        });
      });

    this.socketHandler.disconnect$
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.isConnectedToServer = false;
        this.showSocketWarningMessage = !this.isConnectedToServer;
        this.chRef.detach();
        Observable.timer(0, 10).take(1).subscribe((tick) => {
          this.chRef.detectChanges();
          this.chRef.reattach();
        });
      });

    Observable.timer(1000, 200)
      .take(1)
      .subscribe(() => {
        this.isConnectedToServer = this.socketHandler.isConnected;
        //console.log("Show warning message ", this.showSocketWarningMessage);
        this.showSocketWarningMessage = !this.isConnectedToServer;
        this.chRef.detectChanges();
        this.chRef.detach();
        Observable.timer(0, 10).take(1).subscribe((tick) => {
          this.chRef.detectChanges();
          this.chRef.reattach();
        });
    });

    this.lockControl$()
      .takeUntil(this.destroyed$)
      .withLatestFrom(this.isLoading$)
      .withLatestFrom(this.isModalActive$)
      .subscribe(([[idle, isLoading], isModalActive]) => {
        // Load welcome page.
        if (isLoading || isModalActive) {
          //console.log("isLoading or isModalActive, nothing to do");
          return;
        }
        // fix tablet
        let welcomePage = WELCOME_PAGE;
        if(this.utils.isMobile() || this.utils.isDesktop()){
          welcomePage = 'welcome_local.html';
        }
        document.location.href = welcomePage;
        //this.store.dispatch(this.assessmentActions.lockTest());
      });
  }

  lockControl$(): Observable<any> {
    const newTimer = () => Observable.timer(TIMEOUT_IDLE_LOGIN);
    const mouseMove = Observable.fromEvent(document.body, 'mousemove')
    .startWith("loaded")
    .throttleTime(250);

    return mouseMove.switchMapTo(newTimer());
  }

  refreshPageClick() {
    this.inhibitConnectionProblemMessage = true;
    if(<any>WELCOME_PAGE === 'index.php'){
      // fix tablet
      let welcomePage = WELCOME_PAGE;
      if(this.utils.isMobile() || this.utils.isDesktop()){
        welcomePage = 'welcome_local.html';
      }
      document.location.href = welcomePage;
    } else {
      document.location.reload(true);
    }
  }

  _disableShortcuts() {
    if (DISABLE_SHORTCUT) {
      this.pageEventsHandler.disableBrowserShortcuts();
    }
  }

  toggleInputType(ev){
    this.isPasswordType = !this.isPasswordType;
  }

  onFocus() {
    if (DISABLE_SHORTCUT) {
      this.pageEventsHandler.restoreBrowserShortcuts();
    }
  }

  onBlur() {
    this._disableShortcuts();
  }

  ngOnDestroy() {
    this._disableShortcuts();
    this.destroyed$.next();
  }
}



// WEBPACK FOOTER //
// ./src/app/layout/login/login.component.ts